'use client'

import { type ReactNode, useRef } from 'react'

import { cn } from '@/lib/utils'

/**
 * Pointer-driven 3D tilt.
 *
 * Writes rotation straight to the element's style inside rAF rather than going
 * through React state — a re-render per mousemove would drop frames on a card
 * this size. Children can opt into depth by setting `--depth` and reading it
 * with translateZ, so layers separate as the card turns.
 *
 * Disabled under prefers-reduced-motion and on coarse pointers (a tilt that
 * only fires on tap is noise on touch).
 */

type Props = {
  children: ReactNode
  className?: string
  /** Max rotation in degrees. */
  strength?: number
}

export function TiltCard({ children, className, strength = 7 }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const frame = useRef<number | null>(null)

  const enabled = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const onMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = ref.current
    if (!el || !enabled()) return

    // Freeze the tilt over controls. The rotation moves a button by up to ~9px
    // — a quarter of its height — and because it tracks the cursor, the target
    // keeps sliding as you reach for it. A click only fires when press and
    // release land on the same element, so a moving button silently ate them
    // and had to be clicked several times. Holding the current angle keeps the
    // effect while the hit target stays put.
    //
    // `data-tilt-through` opts an element out of that freeze. The problem above
    // is a SMALL target sliding away from the cursor; a link stretched over the
    // whole card is the opposite — it is the card, so a few degrees of rotation
    // cannot move it out from under anything. Without the exemption, making a
    // card clickable would switch its tilt off permanently, because every
    // pointer move would land on that overlay.
    const control = (event.target as Element | null)?.closest?.(
      'button, a, input, select, textarea, label',
    )
    if (control && !control.hasAttribute('data-tilt-through')) return

    const rect = el.getBoundingClientRect()
    const px = (event.clientX - rect.left) / rect.width - 0.5
    const py = (event.clientY - rect.top) / rect.height - 0.5
    if (frame.current !== null) return
    frame.current = requestAnimationFrame(() => {
      frame.current = null
      el.style.setProperty('--rx', `${(-py * strength).toFixed(2)}deg`)
      el.style.setProperty('--ry', `${(px * strength).toFixed(2)}deg`)
      // Feeds the sheen so the highlight tracks the cursor.
      el.style.setProperty('--mx', `${((px + 0.5) * 100).toFixed(1)}%`)
      el.style.setProperty('--my', `${((py + 0.5) * 100).toFixed(1)}%`)
    })
  }

  const reset = () => {
    const el = ref.current
    if (!el) return
    el.style.setProperty('--rx', '0deg')
    el.style.setProperty('--ry', '0deg')
  }

  return (
    <div
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={reset}
      className={cn('tilt-card', className)}
      style={{ ['--rx' as string]: '0deg', ['--ry' as string]: '0deg' }}
    >
      {children}
    </div>
  )
}
